// ============================================================================
// TEMPLATE SERVICE
// API wrapper for session templates, phase templates, and workflows
// ============================================================================

import { api } from './api';
import {
  SessionTemplate,
  PhaseTemplate,
  TemplateSnapshot,
  BrowseTemplatesParams,
  CreateSessionTemplateInput,
  CreatePhaseTemplateInput,
  TemplateReviewQueueEntry
} from '../types';

// ============================================================================
// SESSION TEMPLATES
// ============================================================================

/**
 * Browse session templates with filters
 */
export async function browseSessionTemplates(
  params?: BrowseTemplatesParams
): Promise<{ templates: SessionTemplate[]; total: number }> {
  const response = await api.get('/api/session-templates/catalog', { params });
  return {
    templates: response.data.templates,
    total: response.data.count
  };
}

/**
 * Get a specific session template by ID and version
 */
export async function getSessionTemplate(
  sessionTemplateId: string,
  versionNumber?: number
): Promise<SessionTemplate> {
  const response = await api.get(`/api/session-templates/catalog/${sessionTemplateId}`, {
    params: versionNumber ? { versionNumber } : undefined
  });
  return response.data.template;
}

/**
 * Get version history for a session template
 */
export async function getSessionTemplateVersions(
  sessionTemplateId: string
): Promise<SessionTemplate[]> {
  const response = await api.get(`/api/session-templates/catalog/${sessionTemplateId}/versions`);
  return response.data.versions;
}

/**
 * Create a new session template
 */
export async function createSessionTemplate(
  input: CreateSessionTemplateInput
): Promise<SessionTemplate> {
  const response = await api.post('/api/session-templates/catalog', input);
  return response.data.template;
}

/**
 * Update a session template (creates new version)
 */
export async function updateSessionTemplate(
  sessionTemplateId: string,
  updates: Partial<CreateSessionTemplateInput> & { changeNotes?: string }
): Promise<SessionTemplate> {
  const response = await api.put(`/api/session-templates/catalog/${sessionTemplateId}`, updates);
  return response.data.template;
}

/**
 * Delete a session template (draft only)
 */
export async function deleteSessionTemplate(
  sessionTemplateId: string,
  versionNumber: number
): Promise<void> {
  await api.delete(`/api/session-templates/catalog/${sessionTemplateId}`, {
    params: { versionNumber }
  });
}

/**
 * Preview a session template with full phase details
 */
export async function previewSessionTemplate(
  sessionTemplateId: string,
  versionNumber?: number
): Promise<{ template: SessionTemplate; snapshot: TemplateSnapshot }> {
  const response = await api.get(`/api/session-templates/catalog/${sessionTemplateId}/preview`, {
    params: versionNumber ? { versionNumber } : undefined
  });
  return {
    template: response.data.template,
    snapshot: response.data.snapshot
  };
}

/**
 * Create a template snapshot (for testing before creating session)
 */
export async function createTemplateSnapshot(
  sessionTemplateId: string,
  versionNumber?: number
): Promise<TemplateSnapshot> {
  const response = await api.post(`/api/session-templates/catalog/${sessionTemplateId}/snapshot`, null, {
    params: versionNumber ? { versionNumber } : undefined
  });
  return response.data.snapshot;
}

// ============================================================================
// PHASE TEMPLATES
// ============================================================================

/**
 * Browse phase templates with filters
 */
export async function browsePhaseTemplates(
  params?: BrowseTemplatesParams
): Promise<{ templates: PhaseTemplate[]; total: number }> {
  const response = await api.get('/api/phase-templates/catalog', { params });
  return {
    templates: response.data.phases,
    total: response.data.count
  };
}

/**
 * Get a specific phase template by ID and version
 */
export async function getPhaseTemplate(
  phaseTemplateId: string,
  versionNumber?: number
): Promise<PhaseTemplate> {
  const response = await api.get(`/api/phase-templates/catalog/${phaseTemplateId}`, {
    params: versionNumber ? { versionNumber } : undefined
  });
  return response.data.phase;
}

/**
 * Create a new phase template
 */
export async function createPhaseTemplate(
  input: CreatePhaseTemplateInput
): Promise<PhaseTemplate> {
  const response = await api.post('/api/phase-templates/catalog', input);
  return response.data.phase;
}

/**
 * Update a phase template (creates new version)
 */
export async function updatePhaseTemplate(
  phaseTemplateId: string,
  updates: Partial<CreatePhaseTemplateInput> & { changeNotes?: string }
): Promise<PhaseTemplate> {
  const response = await api.put(`/api/phase-templates/catalog/${phaseTemplateId}`, updates);
  return response.data.phase;
}

/**
 * Delete a phase template (draft only)
 */
export async function deletePhaseTemplate(
  phaseTemplateId: string,
  versionNumber: number
): Promise<void> {
  await api.delete(`/api/phase-templates/catalog/${phaseTemplateId}`, {
    params: { versionNumber }
  });
}

// ============================================================================
// TEMPLATE PUBLISHING
// ============================================================================

/**
 * Submit a template for review (USER → ORGANIZATION or ORGANIZATION → SYSTEM)
 */
export async function submitTemplateForReview(
  templateType: 'PHASE' | 'SESSION',
  templateId: string,
  versionNumber: number,
  targetScope: 'ORGANIZATION' | 'SYSTEM',
  submissionNotes?: string
): Promise<TemplateReviewQueueEntry> {
  const endpoint = templateType === 'PHASE' ? 'phase-templates' : 'session-templates';
  const response = await api.post(`/api/${endpoint}/catalog/${templateId}/publish`, {
    versionNumber,
    targetScope,
    submissionNotes
  });
  return response.data.review;
}

/**
 * Get pending reviews (template admin only)
 */
export async function getPendingReviews(
  templateType?: 'PHASE' | 'SESSION'
): Promise<TemplateReviewQueueEntry[]> {
  if (templateType === 'PHASE') {
    const response = await api.get('/api/phase-templates/pending-review');
    return response.data.reviews;
  } else if (templateType === 'SESSION') {
    const response = await api.get('/api/session-templates/pending-review');
    return response.data.reviews;
  } else {
    // Fetch both types and combine
    const [phaseResponse, sessionResponse] = await Promise.all([
      api.get('/api/phase-templates/pending-review'),
      api.get('/api/session-templates/pending-review')
    ]);
    return [...phaseResponse.data.reviews, ...sessionResponse.data.reviews];
  }
}

/**
 * Approve a template review (template admin only)
 */
export async function approveTemplateReview(
  templateType: 'PHASE' | 'SESSION',
  reviewId: string,
  reviewNotes?: string
): Promise<void> {
  const endpoint = templateType === 'PHASE' ? 'phase-templates' : 'session-templates';
  await api.post(`/api/${endpoint}/${reviewId}/approve`, { reviewNotes });
}

/**
 * Reject a template review (template admin only)
 */
export async function rejectTemplateReview(
  templateType: 'PHASE' | 'SESSION',
  reviewId: string,
  rejectionReason: string,
  reviewNotes?: string
): Promise<void> {
  const endpoint = templateType === 'PHASE' ? 'phase-templates' : 'session-templates';
  await api.post(`/api/${endpoint}/${reviewId}/reject`, { rejectionReason, reviewNotes });
}

/**
 * Get user's submitted reviews
 */
export async function getMyReviews(): Promise<TemplateReviewQueueEntry[]> {
  const response = await api.get('/templates/my-reviews');
  return response.data.reviews;
}

/**
 * Submit interaction data for pending interactive step
 */
export async function submitInteractionData(
  projectId: string,
  workflowId: string,
  executionId: string,
  stepId: string,
  data: any
): Promise<void> {
  const response = await api.post(
    `/api/projects/${projectId}/workflows/${workflowId}/executions/${executionId}/interaction`,
    { stepId, data }
  );
  return response.data;
}

/**
 * Send conversation message and get avatar response
 */
export async function sendConverseMessage(
  projectId: string,
  workflowId: string,
  executionId: string,
  stepId: string,
  message: string
): Promise<{ role: string; content: string; timestamp: string }> {
  const response = await api.post(
    `/api/projects/${projectId}/workflows/${workflowId}/executions/${executionId}/conversation/message`,
    { stepId, message }
  );
  return response.data.message;
}

/**
 * Update context during conversation
 */
export async function updateConverseContext(
  projectId: string,
  workflowId: string,
  executionId: string,
  stepId: string,
  contextUpdates: Record<string, any>
): Promise<void> {
  const response = await api.patch(
    `/api/projects/${projectId}/workflows/${workflowId}/executions/${executionId}/conversation/context`,
    { stepId, contextUpdates }
  );
  return response.data;
}

/**
 * Complete conversation and resume workflow
 */
export async function completeConversation(
  projectId: string,
  workflowId: string,
  executionId: string,
  stepId: string,
  finalContext: Record<string, any>
): Promise<void> {
  const response = await api.post(
    `/api/projects/${projectId}/workflows/${workflowId}/executions/${executionId}/conversation/complete`,
    { stepId, finalContext }
  );
  return response.data;
}

// ============================================================================
// SESSION CREATION FROM TEMPLATE
// ============================================================================

/**
 * Create a session from a template
 */
export async function createSessionFromTemplate(input: {
  templateId: string;
  templateVersion?: number;
  customerId: string;
  customerName: string;
  goals: string[];
  background?: {
    customer: string;
    industry: string;
    context: string;
  };
  attendees?: Array<{
    name: string;
    role: string;
  }>;
  avatarGroupId?: string;
  customAvatars?: Array<{
    avatarDefinitionId: string;
    versionNumber: number;
    slotNumber: number;
  }>;
  projectId?: string; // Optional project association
  roomId?: string;    // Optional room binding
}): Promise<{
  sessionId: string;
  session: any;
}> {
  const response = await api.post('/api/sessions/from-template', input);
  return {
    sessionId: response.data.sessionId,
    session: response.data
  };
}
